import { useState, useEffect, useCallback } from "react";
import HeaderPanel from "./components/HeaderPanel";
import PixelFaceCanvas from "./components/PixelFaceCanvas";
import CustomizationPanel from "./components/CustomizationPanel";
import LikertScale from "./components/LikertScale";
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface Question {
  id: string;
  text: string;
  classification_id: string;
  polarity: string;
}

export default function Home() {
  const { user, loading, updateCoins } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const [similarityScore, setSimilarityScore] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [likertValue, setLikertValue] = useState(50);
  const [isCustomizationOpen, setIsCustomizationOpen] = useState(false);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [isProcessingAnswer, setIsProcessingAnswer] = useState(false);
  
  const [skinTone, setSkinTone] = useState("#FFD1B3");
  const [hairStyle, setHairStyle] = useState("short");
  const [accessory, setAccessory] = useState("none");
  const [outfit, setOutfit] = useState("casual");

  // Calcular score de similitud
  const calculateSimilarityScore = useCallback(async () => {
    if (!user) return;

    try {
      const { data: classifications } = await supabase
        .from("classifications")
        .select("id");

      const { data: questions } = await supabase
        .from("questions")
        .select("id, classification_id, polarity");

      const { data: userResponses } = await supabase
        .from("user_responses")
        .select("question_id, score")
        .eq("user_id", user.id);

      const { data: allResponses } = await supabase
        .from("user_responses")
        .select("question_id, score");

      if (!classifications || !questions || !userResponses || userResponses.length === 0) {
        setSimilarityScore(0);
        return;
      }

      const questionMap = new Map(
        questions.map((q) => [q.id, { classificationId: q.classification_id, polarity: q.polarity }])
      );

      const differences: number[] = [];

      classifications.forEach((classification) => {
        const classQuestionIds = questions
          .filter((q) => q.classification_id === classification.id)
          .map((q) => q.id);

        if (classQuestionIds.length === 0) return;

        const userScoresForClass = userResponses.filter((r) =>
          classQuestionIds.includes(r.question_id)
        );

        if (userScoresForClass.length === 0) return;

        const adjustedUserScores = userScoresForClass.map((r) => {
          const questionInfo = questionMap.get(r.question_id);
          return questionInfo?.polarity === "negative" ? 100 - r.score : r.score;
        });

        const userAverage = adjustedUserScores.reduce((a, b) => a + b, 0) / adjustedUserScores.length;

        const globalScoresForClass = allResponses?.filter((r) =>
          classQuestionIds.includes(r.question_id)
        ) || [];

        const adjustedGlobalScores = globalScoresForClass.map((r) => {
          const questionInfo = questionMap.get(r.question_id);
          return questionInfo?.polarity === "negative" ? 100 - r.score : r.score;
        });

        const globalAverage = adjustedGlobalScores.length > 0
          ? adjustedGlobalScores.reduce((a, b) => a + b, 0) / adjustedGlobalScores.length
          : 50;

        differences.push(Math.abs(userAverage - globalAverage));
      });

      if (differences.length > 0) {
        const avgDifference = differences.reduce((sum, d) => sum + d, 0) / differences.length;
        setSimilarityScore(Math.round(100 - avgDifference));
      }
    } catch (error) {
      console.error("Error calculando similitud:", error);
    }
  }, [user]);

  const loadRandomQuestion = useCallback(async () => {
    if (!user || isProcessingAnswer) return;
    
    setLoadingQuestion(true);
    try {
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

      const { data: recentResponses } = await supabase
        .from('user_responses')
        .select('question_id')
        .eq('user_id', user.id)
        .gte('answered_at', threeMonthsAgo.toISOString());

      const answeredQuestionIds = recentResponses?.map(r => r.question_id) || [];

      let query = supabase
        .from('questions')
        .select('*');

      if (answeredQuestionIds.length > 0) {
        query = query.not('id', 'in', `(${answeredQuestionIds.join(',')})`);
      }

      const { data: questions, error } = await query;

      if (error) throw error;

      if (questions && questions.length > 0) {
        const randomIndex = Math.floor(Math.random() * questions.length);
        setCurrentQuestion(questions[randomIndex]);
      } else {
        setCurrentQuestion(null);
      }
    } catch (error) {
      console.error('Error cargando pregunta:', error);
    } finally {
      setLoadingQuestion(false);
    }
  }, [user, isProcessingAnswer]);

  useEffect(() => {
    if (user && !currentQuestion && !loadingQuestion && !isProcessingAnswer) {
      loadRandomQuestion();
      calculateSimilarityScore();
    }
  }, [user]);

  const handleSliderRelease = async () => {
    if (!user || !currentQuestion || isProcessingAnswer) return;

    setIsProcessingAnswer(true);
    
    try {
      const { error: responseError } = await supabase
        .from('user_responses')
        .insert([
          {
            user_id: user.id,
            question_id: currentQuestion.id,
            score: likertValue,
            answered_at: new Date().toISOString()
          }
        ]);

      if (responseError) throw responseError;

      await updateCoins(10);
      await calculateSimilarityScore();

      setLikertValue(50);

      setTimeout(() => {
        loadRandomQuestion();
        setIsProcessingAnswer(false);
      }, 300);
    } catch (error) {
      console.error('Error guardando respuesta:', error);
      setIsProcessingAnswer(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex flex-col overflow-hidden">
      <HeaderPanel similarityScore={similarityScore} />
      
      {/* Contenedor principal con flex que distribuye el espacio */}
      <div className="flex-1 flex flex-col min-h-0 px-4 pt-2 pb-3">
        {/* Área del personaje - ocupa el espacio disponible */}
        <div className="flex-1 min-h-0 flex items-start justify-center">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-3 flex flex-col items-center relative w-full max-w-md h-full">
            <button
              onClick={() => setIsCustomizationOpen(!isCustomizationOpen)}
              className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200 transition-all z-40"
              title="Personalizar personaje"
            >
              <i className={`ri-${isCustomizationOpen ? 'close-line' : 'palette-line'} text-lg`}></i>
            </button>
            
            <div className="flex-1 flex items-start justify-center overflow-hidden pt-1">
              <PixelFaceCanvas
                skinTone={skinTone}
                expression={likertValue}
                showFullBody={isCustomizationOpen}
              />
            </div>

            {isCustomizationOpen && (
              <div className="w-full mt-2 pt-2 border-t border-gray-100">
                <CustomizationPanel
                  selectedSkinTone={skinTone}
                  selectedHairStyle={hairStyle}
                  selectedAccessory={accessory}
                  selectedOutfit={outfit}
                  onSkinToneChange={setSkinTone}
                  onHairStyleChange={setHairStyle}
                  onAccessoryChange={setAccessory}
                  onOutfitChange={setOutfit}
                />
              </div>
            )}
          </div>
        </div>

        {/* Área del Likert - altura fija en la parte inferior */}
        <div className="flex-shrink-0 mt-2 max-w-md mx-auto w-full">
          {loadingQuestion || isProcessingAnswer ? (
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 flex items-center justify-center">
              <div className="text-center">
                <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-gray-600 text-sm">
                  {isProcessingAnswer ? 'Guardando...' : 'Cargando...'}
                </p>
              </div>
            </div>
          ) : currentQuestion ? (
            <LikertScale
              question={currentQuestion.text}
              currentValue={likertValue}
              onValueChange={setLikertValue}
              onRelease={handleSliderRelease}
            />
          ) : (
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-3">
                <i className="ri-checkbox-circle-fill text-green-500 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">¡Felicidades!</h3>
              <p className="text-gray-600 text-sm">Has respondido todas las preguntas. Vuelve en 3 meses.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
